import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../contexts/store-provider';
import { useOperatorTaskContext } from '../context/operator-task-context';
import {
  getTenantIdFromLocalStorage,
  getToolBOMFactoryIdFromLocalStorage,
  clearToolBOMFactoryIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';

interface IProps {
  onGetClick?: () => void;
}

/**
* This function is used to turn the stored Teachmebiz URL into a link target. The URL is not
* validated when it is saved, so it is opened as entered: a value without a scheme gets https://
* and script-type schemes are not
* turned into a link.
* @param url: Teachmebiz URL as stored
*/
const toLinkHref = (url?: string): string | null => {
  const value = (url || '').trim();
  if (!value) return null;
  if (/^(javascript|data|vbscript):/i.test(value)) return null;
  return /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value.replace(/^\/+/, '')}`;
};

/**
* This functional component renders the Operator Assign Task "Tool BOM" tab's left table
* @param props: onGetClick - called when a row's Get button is clicked, so the host view can
* activate the Target Tool List accordion item
*/
const ToolBomRelationTable = ({ onGetClick }: IProps) => {
  // Tray opened on the Operator Assign Task screen; the relation list is loaded for it
  const { selectedTrayId: trayId } = useOperatorTaskContext();
  const [isFactoryReady, setIsFactoryReady] = useState(false);
  const { toolBOMStore } = useStore();
  const {
    toolBomRelationInfoList,
    getToolBomRelationInfoState,
    GetFactoryIdByTenantIdService,
    GetToolBomRelationInfoService,
    GetToolBomToolManagementListService,
    resetToolBomRelationInfo,
    resetToolBomToolManagementList,
  } = toolBOMStore;

  useEffect(() => {
    GetFactoryIdByTenantIdService(Number(getTenantIdFromLocalStorage())).then(() => {
      setIsFactoryReady(true);
    });
    return () => {
      resetToolBomRelationInfo();
      resetToolBomToolManagementList();
      clearToolBOMFactoryIdFromLocalStorage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the relation list for the opened tray, and reload it when another tray is opened
  useEffect(() => {
    if (!isFactoryReady) return;
    if (!trayId) {
      resetToolBomRelationInfo();
      return;
    }
    GetToolBomRelationInfoService(getToolBOMFactoryIdFromLocalStorage() || '', trayId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFactoryReady, trayId]);

  const handleGetClick = (toolKatashiki: string | undefined) => {
    GetToolBomToolManagementListService(
      toolKatashiki || '',
      getToolBOMFactoryIdFromLocalStorage() || '',
      Number(getTenantIdFromLocalStorage())
    );
    onGetClick?.();
  };

  return (
    <div className="tab_block border rounded tool-bom-relation-tab">
      <div className="tableDesign operator-tool-bom-table">
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table table-hover table-bordered">
              <thead>
                <tr>
                  <th><FormattedMessage id="material_product_id" /></th>
                  <th><FormattedMessage id="material_katashiki" /></th>
                  <th><FormattedMessage id="category" /></th>
                  <th><FormattedMessage id="tool_katashiki_model" /></th>
                  <th><FormattedMessage id="tool_name_equipment_name" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {toolBomRelationInfoList && toolBomRelationInfoList.length > 0 ? (
                  toolBomRelationInfoList.map((row, index) => (
                    <tr key={index}>
                      <td title={row.MaterialProductId || undefined}>
                        {row.MaterialProductId && toLinkHref(row.TeachmebizUrl) ? (
                          <a
                            href={toLinkHref(row.TeachmebizUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.MaterialProductId}
                          </a>
                        ) : (
                          row.MaterialProductId
                        )}
                      </td>
                      <td title={row.MaterialKatashiki1 || undefined}>{row.MaterialKatashiki1}</td>
                      <td title={row.Category || undefined}>{row.Category}</td>
                      <td title={row.ToolModel || undefined}>
                        {row.ToolModel && toLinkHref(row.ToolTeachmebizUrl) ? (
                          <a
                            href={toLinkHref(row.ToolTeachmebizUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.ToolModel}
                          </a>
                        ) : (
                          row.ToolModel
                        )}
                      </td>
                      <td title={row.ToolName || undefined}>{row.ToolName}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleGetClick(row.ToolModel)}
                        >
                          <FormattedMessage id="get" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  !getToolBomRelationInfoState.inProgress && (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center"
                      >
                        <FormattedMessage id="no_tool_bom_relation_data" />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(ToolBomRelationTable);
